import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { GlassCard } from '../components/GlassCard';
import { useUserStore } from '../store/userStore';
import { logWeight, getWeightHistory } from '../lib/api';

function bmiCategory(bmi) {
  if (bmi < 18.5) return { label: 'Underweight', color: '#3B82F6' };
  if (bmi < 25) return { label: 'Healthy', color: '#10B981' };
  if (bmi < 30) return { label: 'Overweight', color: '#F59E0B' };
  return { label: 'Obese', color: '#EF4444' };
}

export default function WeightTracker() {
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () =>
    getWeightHistory(user.id).then((d) => {
      setEntries(d.entries || []);
      if (d.height) setHeight(String(d.height));
    }).catch(() => {});

  useEffect(() => {
    if (user?.id) load();
  }, [user?.id]); // eslint-disable-line

  const save = async () => {
    if (!weight) {
      toast.error('Enter your weight');
      return;
    }
    setSaving(true);
    try {
      await logWeight({ user_id: user.id, weight: parseFloat(weight), height: height ? parseFloat(height) : undefined });
      toast.success('Weight logged');
      setWeight('');
      load();
    } catch (e) {
      toast.error('Could not save');
    } finally {
      setSaving(false);
    }
  };

  const latest = entries[0];
  const cat = latest?.bmi ? bmiCategory(latest.bmi) : null;

  return (
    <div className="min-h-screen" style={{ background: 'var(--aura-bg)' }}>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/app')} className="grid place-items-center w-9 h-9 rounded-full bg-white border border-[var(--aura-line)]"><i className="fa-solid fa-arrow-left" /></button>
          <h1 className="font-display text-2xl sm:text-3xl">Weight &amp; BMI</h1>
        </div>

        {latest && (
          <GlassCard className="p-6 mb-5 flex items-center gap-6">
            <div>
              <div className="text-xs text-[var(--aura-ink-soft)]">Latest weight</div>
              <div className="font-display text-3xl">{latest.weight} <span className="text-lg text-[var(--aura-ink-soft)]">kg</span></div>
            </div>
            {latest.bmi && (
              <div className="pl-6 border-l border-[var(--aura-line)]">
                <div className="text-xs text-[var(--aura-ink-soft)]">BMI</div>
                <div className="font-display text-3xl" style={{ color: cat.color }}>{latest.bmi}</div>
                <span className="text-xs font-semibold" style={{ color: cat.color }}>{cat.label}</span>
              </div>
            )}
          </GlassCard>
        )}

        <GlassCard className="p-6 mb-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[var(--aura-ink-soft)] mb-1">Weight (kg)</label>
              <input data-testid="weight-input" type="number" step="any" value={weight} onChange={(e) => setWeight(e.target.value)} className="w-full rounded-xl border border-[var(--aura-line)] px-4 py-3 outline-none focus:border-[var(--aura-purple)]" placeholder="68" />
            </div>
            <div>
              <label className="block text-sm text-[var(--aura-ink-soft)] mb-1">Height (cm)</label>
              <input data-testid="height-input" type="number" step="any" value={height} onChange={(e) => setHeight(e.target.value)} className="w-full rounded-xl border border-[var(--aura-line)] px-4 py-3 outline-none focus:border-[var(--aura-purple)]" placeholder="165" />
            </div>
          </div>
          <button data-testid="log-weight-btn" onClick={save} disabled={saving} className="mt-4 aura-btn-gradient text-white font-semibold rounded-2xl px-6 py-3 flex items-center gap-2 disabled:opacity-70">
            {saving ? <i className="fa-solid fa-circle-notch fa-spin" /> : <><i className="fa-solid fa-plus" /> Log entry</>}
          </button>
        </GlassCard>

        {entries.length > 0 && (
          <div>
            <h3 className="font-display text-xl mb-3">History</h3>
            <div className="flex flex-col gap-2">
              {entries.map((e, i) => (
                <GlassCard key={e.id || i} className="p-4 flex items-center justify-between">
                  <span className="text-sm text-[var(--aura-ink-soft)]">{e.date}</span>
                  <span className="font-semibold">{e.weight} kg {e.bmi ? <span className="text-[var(--aura-ink-soft)] font-normal">· BMI {e.bmi}</span> : null}</span>
                </GlassCard>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
